import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const DEFAULT_TABLE_QUERY = { search: '', page: 1, limit: 10 };

/**
 * Query state + fetching for a list screen.
 *
 * `fetcher(query)` owns the actual request, so each screen keeps its own URL
 * shape (params object, `?page=&limit=`, `&pageSize=` ...) while search
 * debouncing, loading flags, page resets and refetching live in one place.
 *
 * @param {function} fetcher  (query) => rows | { data: rows } | Promise thereof
 * @param {Array}    deps     extra values that should trigger a refetch
 * @param {object}   options  { initialQuery, debounceMs, enabled }
 */
export default function useTableData(fetcher, deps = [], options = {}) {
  const { initialQuery = DEFAULT_TABLE_QUERY, debounceMs = 400, enabled = true } = options;

  const [query, setQuery] = useState({ ...DEFAULT_TABLE_QUERY, ...initialQuery });
  const [applied, setApplied] = useState(query);
  const [rows, setRows] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(enabled));
  const [reloadToken, setReloadToken] = useState(0);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // Debounce only what the user types; page/limit changes apply immediately.
  const appliedKey = JSON.stringify(applied);
  const queryKey = JSON.stringify(query);
  useEffect(() => {
    if (queryKey === appliedKey) return undefined;
    const immediate = query.search === applied.search;
    if (immediate) {
      setApplied(query);
      return undefined;
    }
    const timer = setTimeout(() => setApplied(query), debounceMs);
    return () => clearTimeout(timer);
  }, [queryKey, appliedKey, debounceMs]); // eslint-disable-line react-hooks/exhaustive-deps

  const depsKey = JSON.stringify(deps);
  useEffect(() => {
    if (!enabled) {
      setRows([]);
      setIsLoading(false);
      return undefined;
    }
    let cancelled = false;
    setIsLoading(true);
    setRows(null);

    Promise.resolve(fetcherRef.current(applied))
      .then((result) => {
        if (cancelled) return;
        const payload = Array.isArray(result) ? result : result?.data;
        setRows(Array.isArray(payload) ? payload : []);
        setIsLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        console.log('Table fetch failed', error);
        setRows([]);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [appliedKey, depsKey, reloadToken, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const patchQuery = useCallback((patch) => setQuery((prev) => ({ ...prev, ...patch })), []);
  const setSearch = useCallback((search) => patchQuery({ search, page: 1 }), [patchQuery]);
  const setPage = useCallback((page) => patchQuery({ page: Math.max(1, Number(page) || 1) }), [patchQuery]);
  const setLimit = useCallback((limit) => patchQuery({ limit: Number(limit) || 10, page: 1 }), [patchQuery]);
  const reload = useCallback(() => setReloadToken((n) => n + 1), []);

  // Server endpoints here return a page of rows without a total count, so the
  // last page is the one that comes back short.
  const hasNextPage = (rows?.length || 0) >= Number(applied.limit || 10);
  const totalPages = useMemo(
    () => (hasNextPage ? Number(applied.page || 1) + 1 : Number(applied.page || 1)),
    [hasNextPage, applied.page]
  );

  return {
    rows: rows || [],
    rawRows: rows,
    isLoading,
    query,
    appliedQuery: applied,
    setQuery,
    patchQuery,
    setSearch,
    setPage,
    setLimit,
    reload,
    hasNextPage,
    totalPages,
  };
}

/**
 * Same idea for screens whose rows are already in memory (redux, a parent
 * fetch, a static list): search + paginate on the client.
 */
export function useClientTable(allRows, { searchKeys, initialQuery = DEFAULT_TABLE_QUERY } = {}) {
  const [query, setQuery] = useState({ ...DEFAULT_TABLE_QUERY, ...initialQuery });

  const filtered = useMemo(() => {
    const source = Array.isArray(allRows) ? allRows : [];
    const term = (query.search || '').trim().toLowerCase();
    if (!term) return source;
    return source.filter((row) => {
      const fields = searchKeys?.length ? searchKeys.map((key) => row?.[key]) : Object.values(row || {});
      return fields.some((value) => value != null && String(value).toLowerCase().includes(term));
    });
  }, [allRows, query.search, searchKeys]);

  const limit = Number(query.limit) || 10;
  const totalPages = Math.max(1, Math.ceil(filtered.length / limit));
  const page = Math.min(Math.max(1, Number(query.page) || 1), totalPages);
  const rows = useMemo(() => filtered.slice((page - 1) * limit, page * limit), [filtered, page, limit]);

  const patchQuery = useCallback((patch) => setQuery((prev) => ({ ...prev, ...patch })), []);

  return {
    rows,
    // Every row that matches the current search (all pages) — used for exports.
    filteredRows: filtered,
    total: filtered.length,
    query: { ...query, page },
    page,
    totalPages,
    rowsPerPage: limit,
    setSearch: useCallback((search) => patchQuery({ search, page: 1 }), [patchQuery]),
    setPage: useCallback((next) => patchQuery({ page: next }), [patchQuery]),
    setLimit: useCallback((next) => patchQuery({ limit: Number(next) || 10, page: 1 }), [patchQuery]),
    patchQuery,
  };
}
